import { and, desc, eq, gt, inArray, isNull, lte, or, sql } from "drizzle-orm";
import { randomUUID } from "node:crypto";

import { schema } from "@/integrations/drizzle";

import type { AiAction, QuotaAiJobStatus, QuotaLedgerEntryRecord } from "../quota";
import type {
  AbnormalPaymentEventRecord,
  BillingPackageRecord,
  OrderRecord,
  PaymentEventRecord,
  Phase4PaddleEnvironment,
  PaymentProviderEnvironment,
  PaymentProviderId,
  PurchaseWhitelistRecord,
  QuotaHistoryLedgerEntryRecord,
} from "./types";

import { PAID_QUOTA_VALIDITY_DAYS, PAID_QUOTA_VALIDITY_MS } from "./config";
import { providerPaymentIdentityLockKey } from "./provider-payment-lock";

async function getDefaultDb() {
  const { db } = await import("@/integrations/drizzle/client");
  return db;
}

export type CreateOrderInput = {
  userId: string;
  shortId: string;
  package: BillingPackageRecord;
  whitelist: PurchaseWhitelistRecord;
  disclosureVersions: Record<string, unknown>;
  idempotencyKey: string;
  provider: PaymentProviderId;
  providerEnvironment: PaymentProviderEnvironment;
  now: Date;
};

export type CheckoutAttachmentInput = {
  orderId: string;
  paddleTransactionId: string;
  paddleCustomerId?: string | null;
  checkoutUrl?: string | null;
  now: Date;
  expectedUpdatedAt?: Date;
  expectedProvider?: PaymentProviderId;
  expectedProviderEnvironment?: PaymentProviderEnvironment;
  expectedStatus?: CheckoutAttachmentStatus;
};

export type ProviderCheckoutAttachmentInput = {
  orderId: string;
  providerStoreId: string;
  providerProductId: string;
  providerCheckoutSessionId: string;
  providerCheckoutUrl: string;
  providerCheckoutExpiresAt?: Date | null;
  orderMerchantExternalId: string;
  now: Date;
  expectedUpdatedAt?: Date;
  expectedProvider?: PaymentProviderId;
  expectedProviderEnvironment?: PaymentProviderEnvironment;
  expectedStatus?: CheckoutAttachmentStatus;
  checkoutDispatchLeaseId?: string;
};

export type WaffoCheckoutFenceClaimInput = CreateOrderInput & {
  leaseMs?: number;
};

export type WaffoCheckoutFenceClaim =
  | { kind: "claimed"; order: OrderRecord; checkoutDispatchLeaseId: string }
  | { kind: "ready"; order: OrderRecord }
  | { kind: "in_progress"; order: OrderRecord }
  | { kind: "needs_review"; order: OrderRecord };

export type CreatePaymentEventInput = {
  environment: Phase4PaddleEnvironment;
  paddleEventId: string;
  paddleNotificationId?: string | null;
  eventType: string;
  paddleObjectId?: string | null;
  paddleTransactionId?: string | null;
  orderId?: string | null;
  occurredAt?: Date | null;
  receivedAt: Date;
  signatureStatus: "verified" | "provider_inquiry_verified" | "failed";
  processingStatus: string;
  redactedPayload?: Record<string, unknown> | null;
  errorType?: string | null;
  errorMessage?: string | null;
  idempotencyKey: string;
};

export type CreateProviderPaymentEventInput = {
  provider: PaymentProviderId;
  environment: PaymentProviderEnvironment;
  providerDeliveryId: string;
  providerBusinessEventId: string;
  eventType: string;
  providerStoreId?: string | null;
  orderMerchantExternalId?: string | null;
  providerOrderId?: string | null;
  providerPaymentId?: string | null;
  providerRefundTicketId?: string | null;
  providerRefundId?: string | null;
  orderId?: string | null;
  occurredAt?: Date | null;
  receivedAt: Date;
  signatureStatus: "verified" | "failed";
  processingStatus: string;
  redactedPayload?: Record<string, unknown> | null;
  errorType?: string | null;
  errorMessage?: string | null;
  idempotencyKey: string;
};

export type ProviderPaymentEventConflict = {
  reasonCode: "waffo_provider_identity_conflict";
  summary: "Waffo provider identity fields conflict with an existing durable event.";
};

export type CreateAbnormalPaymentEventInput = {
  userId?: string | null;
  orderId?: string | null;
  paymentEventId?: string | null;
  quotaLedgerEntryId?: string | null;
  reasonCode: string;
  summary: string;
  severity?: "low" | "medium" | "high" | "stop_line";
  now: Date;
};

export type GrantPaidQuotaInput = {
  order: OrderRecord;
  paymentEvent: PaymentEventRecord;
  providerOrderId?: string | null;
  providerPaymentId?: string | null;
  processingLeaseId?: string | null;
  now: Date;
};

export type PaymentStore = {
  listLedgerEntries(input: { userId: string }): Promise<QuotaHistoryLedgerEntryRecord[]>;
  findPurchaseWhitelist(input: { userId: string }): Promise<PurchaseWhitelistRecord | null>;
  findActivePackage(input: {
    packageId: string;
    environment: Phase4PaddleEnvironment;
  }): Promise<BillingPackageRecord | null>;
  listOrders(input: { userId: string; limit?: number }): Promise<OrderRecord[]>;
  findOrderForUser(input: { userId: string; orderId: string }): Promise<OrderRecord | null>;
  countGrantedOrdersForPackage(input: { userId: string; packageId: string }): Promise<number>;
  findPendingOrder(input: { userId: string; packageId?: string }): Promise<OrderRecord | null>;
  findOrderByIdempotencyKey(input: { idempotencyKey: string }): Promise<OrderRecord | null>;
  createOrder(input: CreateOrderInput): Promise<OrderRecord>;
  claimWaffoCheckoutFence(input: WaffoCheckoutFenceClaimInput): Promise<WaffoCheckoutFenceClaim>;
  markWaffoCheckoutDispatchStarted(input: {
    orderId: string;
    checkoutDispatchLeaseId: string;
    now: Date;
  }): Promise<OrderRecord>;
  attachCheckout(input: CheckoutAttachmentInput): Promise<OrderRecord>;
  attachProviderCheckout(input: ProviderCheckoutAttachmentInput): Promise<OrderRecord>;
  markOrderCheckoutFailed(input: {
    orderId: string;
    now: Date;
    errorMessage?: string | null;
    expectedUpdatedAt?: Date;
    expectedProvider?: PaymentProviderId;
    expectedProviderEnvironment?: PaymentProviderEnvironment;
    expectedStatus?: CheckoutAttachmentStatus;
  }): Promise<OrderRecord>;
  findOrderByPaddleTransactionId(input: { paddleTransactionId: string }): Promise<OrderRecord | null>;
  findOrderByProviderLinkage(input: {
    provider: PaymentProviderId;
    environment: PaymentProviderEnvironment;
    orderMerchantExternalId: string;
  }): Promise<OrderRecord | null>;
  findPaymentEventByIdempotencyKey(input: { idempotencyKey: string }): Promise<PaymentEventRecord | null>;
  createPaymentEvent(input: CreatePaymentEventInput): Promise<{ event: PaymentEventRecord; duplicate: boolean }>;
  createProviderPaymentEvent(
    input: CreateProviderPaymentEventInput,
  ): Promise<{ event: PaymentEventRecord; duplicate: boolean; conflict?: ProviderPaymentEventConflict }>;
  recordProviderIdentityConflict(input: {
    paymentEventId: string;
    reasonCode: ProviderPaymentEventConflict["reasonCode"];
    summary: ProviderPaymentEventConflict["summary"];
    now: Date;
  }): Promise<PaymentEventRecord>;
  claimProviderPaymentEvent(input: {
    paymentEventId: string;
    now: Date;
  }): Promise<{ event: PaymentEventRecord; claimed: boolean }>;
  releaseProviderPaymentEventClaim(input: {
    paymentEventId: string;
    processingLeaseId: string;
    now: Date;
  }): Promise<PaymentEventRecord>;
  markPaymentEventProcessed(input: {
    paymentEventId: string;
    orderId?: string | null;
    processingStatus: string;
    errorType?: string | null;
    errorMessage?: string | null;
    now: Date;
  }): Promise<PaymentEventRecord>;
  markOrderPaidProcessing(input: {
    orderId: string;
    providerOrderId?: string | null;
    providerPaymentId?: string | null;
    now: Date;
  }): Promise<OrderRecord>;
  markOrderNeedsReview(input: {
    orderId: string;
    now: Date;
    quotaGrantStatus?: string;
    expectedCheckoutDispatchLeaseId?: string;
    expectedUpdatedAt?: Date;
    expectedStatus?: CheckoutAttachmentStatus;
  }): Promise<OrderRecord>;
  recordRefundOutcome(input: {
    paymentEvent: PaymentEventRecord;
    order?: OrderRecord | null;
    refundStatus?: "refunded" | "failed";
    needsReview?: boolean;
    reasonCode?: string | null;
    summary?: string | null;
    now: Date;
  }): Promise<{
    order: OrderRecord | null;
    paymentEvent: PaymentEventRecord;
    abnormalEvent: AbnormalPaymentEventRecord | null;
  }>;
  grantPaidQuota(
    input: GrantPaidQuotaInput,
  ): Promise<{ order: OrderRecord; ledgerEntry: QuotaLedgerEntryRecord | null; paymentEvent?: PaymentEventRecord }>;
  createAbnormalEvent(input: CreateAbnormalPaymentEventInput): Promise<AbnormalPaymentEventRecord>;
};

export class WaffoProviderBindingConflictError extends Error {
  constructor() {
    super("Waffo provider binding conflicts with the immutable local order authority.");
    this.name = "WaffoProviderBindingConflictError";
  }
}

export class WaffoRefundFirstHoldError extends Error {
  constructor() {
    super("Waffo completion is held because a refund outcome arrived before completion reconciliation.");
    this.name = "WaffoRefundFirstHoldError";
  }
}

export class WaffoPaymentEventClaimLostError extends Error {
  constructor(readonly event: PaymentEventRecord) {
    super("Waffo payment event processing claim was lost before finalization.");
    this.name = "WaffoPaymentEventClaimLostError";
  }
}

export class PaidQuotaGrantAuthorityError extends Error {
  constructor(readonly code: "invalid_validity" | "grant_ledger_mismatch") {
    super("Paid quota grant authority failed closed.");
    this.name = "PaidQuotaGrantAuthorityError";
  }
}

export function getPaidQuotaGrantExpiresAt(order: OrderRecord, now: Date) {
  if (order.packageSnapshot.validityDays !== PAID_QUOTA_VALIDITY_DAYS) {
    throw new PaidQuotaGrantAuthorityError("invalid_validity");
  }
  return new Date(now.getTime() + PAID_QUOTA_VALIDITY_MS);
}

function matchesPaidQuotaGrantAuthority(
  ledger: typeof schema.multiresumeQuotaLedgerEntry.$inferSelect,
  order: typeof schema.multiresumeOrder.$inferSelect,
) {
  return (
    ledger.entryType === "purchase_grant" &&
    ledger.bucket === "paid" &&
    ledger.userId === order.userId &&
    ledger.orderId === order.id &&
    ledger.sourceType === "order" &&
    ledger.sourceId === order.id &&
    ledger.amount === order.expectedQuotaUnits &&
    ledger.idempotencyKey === `quota-grant:order:${order.id}` &&
    ledger.expiresAt instanceof Date &&
    Number.isFinite(ledger.expiresAt.getTime())
  );
}

function mapPackage(row: typeof schema.multiresumeBillingPackage.$inferSelect): BillingPackageRecord {
  return {
    ...row,
    paddleEnvironment: row.paddleEnvironment as Phase4PaddleEnvironment,
    metadata: row.metadata as Record<string, unknown> | null,
  };
}

function mapWhitelist(row: typeof schema.multiresumePurchaseWhitelist.$inferSelect): PurchaseWhitelistRecord {
  return row;
}

function mapOrder(row: typeof schema.multiresumeOrder.$inferSelect): OrderRecord {
  return {
    ...row,
    paddleEnvironment: row.paddleEnvironment as Phase4PaddleEnvironment,
    provider: row.provider as PaymentProviderId,
    providerEnvironment: row.providerEnvironment as PaymentProviderEnvironment,
    packageSnapshot: row.packageSnapshot as Record<string, unknown>,
    purchaseWhitelistSnapshot: row.purchaseWhitelistSnapshot as Record<string, unknown>,
    disclosureVersions: row.disclosureVersions as Record<string, unknown>,
  };
}

function mapPaymentEvent(row: typeof schema.multiresumePaymentEvent.$inferSelect): PaymentEventRecord {
  return {
    ...row,
    provider: row.provider as PaymentProviderId,
    environment: row.environment as PaymentProviderEnvironment,
    redactedPayload: row.redactedPayload as Record<string, unknown> | null,
  };
}

function mapLedgerEntry(row: typeof schema.multiresumeQuotaLedgerEntry.$inferSelect): QuotaLedgerEntryRecord {
  return {
    ...row,
    entryType: row.entryType as QuotaLedgerEntryRecord["entryType"],
    bucket: row.bucket as QuotaLedgerEntryRecord["bucket"],
  };
}

function getUniqueValues(values: Array<string | null | undefined>) {
  return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

function getOrderPackageDisplayName(order: OrderRecord | undefined) {
  if (!order) return null;
  const displayName = order.packageSnapshot.displayName;
  return typeof displayName === "string" && displayName.trim() ? displayName : order.packageId;
}

const PROVIDER_AUTHORITY_PAYLOAD_FIELDS = [
  "occurredAt",
  "orderId",
  "paymentId",
  "orderMerchantExternalId",
  "refundStatus",
  "refundTicketMerchantExternalId",
  "refundId",
  "amount",
  "taxAmount",
  "total",
  "currency",
  "orderStatus",
  "paymentStatus",
] as const;

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (!value || typeof value !== "object") return JSON.stringify(value);
  return `{${Object.keys(value as Record<string, unknown>)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson((value as Record<string, unknown>)[key])}`)
    .join(",")}}`;
}

function authorityPayload(payload: Record<string, unknown> | null | undefined) {
  return Object.fromEntries(PROVIDER_AUTHORITY_PAYLOAD_FIELDS.map((field) => [field, payload?.[field] ?? null]));
}

function providerAuthorityFingerprint(
  input: Pick<
    CreateProviderPaymentEventInput,
    | "eventType"
    | "providerStoreId"
    | "orderMerchantExternalId"
    | "providerOrderId"
    | "providerPaymentId"
    | "providerRefundTicketId"
    | "providerRefundId"
    | "occurredAt"
    | "redactedPayload"
  >,
) {
  return stableJson({
    eventType: input.eventType,
    providerStoreId: input.providerStoreId ?? null,
    orderMerchantExternalId: input.orderMerchantExternalId ?? null,
    providerOrderId: input.providerOrderId ?? null,
    providerPaymentId: input.providerPaymentId ?? null,
    providerRefundTicketId: input.providerRefundTicketId ?? null,
    providerRefundId: input.providerRefundId ?? null,
    occurredAt: input.occurredAt?.toISOString() ?? null,
    payload: authorityPayload(input.redactedPayload),
  });
}

function existingProviderAuthorityFingerprint(row: typeof schema.multiresumePaymentEvent.$inferSelect) {
  return providerAuthorityFingerprint({
    eventType: row.eventType,
    providerStoreId: row.providerStoreId,
    orderMerchantExternalId: row.orderMerchantExternalId,
    providerOrderId: row.providerOrderId,
    providerPaymentId: row.providerPaymentId,
    providerRefundTicketId: row.providerRefundTicketId,
    providerRefundId: row.providerRefundId,
    occurredAt: row.occurredAt,
    redactedPayload: row.redactedPayload as Record<string, unknown> | null,
  });
}

async function enrichLedgerEntriesForHistory(
  ledgerEntries: QuotaLedgerEntryRecord[],
): Promise<QuotaHistoryLedgerEntryRecord[]> {
  if (ledgerEntries.length === 0) return [];

  const db = await getDefaultDb();
  const aiJobIds = getUniqueValues(ledgerEntries.map((entry) => entry.aiJobId));
  const reservationIds = getUniqueValues(ledgerEntries.map((entry) => entry.reservationId));
  const orderIds = getUniqueValues(ledgerEntries.map((entry) => entry.orderId));
  const aiJobRowsById = new Map<string, typeof schema.multiresumeAiJob.$inferSelect>();
  const aiJobRowsByReservationId = new Map<string, typeof schema.multiresumeAiJob.$inferSelect>();

  if (aiJobIds.length > 0) {
    const rows = await db.select().from(schema.multiresumeAiJob).where(inArray(schema.multiresumeAiJob.id, aiJobIds));
    for (const row of rows) aiJobRowsById.set(row.id, row);
  }

  if (reservationIds.length > 0) {
    const rows = await db
      .select()
      .from(schema.multiresumeAiJob)
      .where(inArray(schema.multiresumeAiJob.reservationId, reservationIds));
    for (const row of rows) {
      if (row.reservationId) aiJobRowsByReservationId.set(row.reservationId, row);
    }
  }

  const aiJobRows = [...aiJobRowsById.values(), ...aiJobRowsByReservationId.values()];
  const resumeIds = getUniqueValues(aiJobRows.flatMap((row) => [row.resumeId, row.targetResumeId]));
  const resumeNamesById = new Map<string, string>();

  if (resumeIds.length > 0) {
    const rows = await db
      .select({ id: schema.resume.id, name: schema.resume.name })
      .from(schema.resume)
      .where(inArray(schema.resume.id, resumeIds));
    for (const row of rows) resumeNamesById.set(row.id, row.name);
  }

  const ordersById = new Map<string, OrderRecord>();
  if (orderIds.length > 0) {
    const rows = await db.select().from(schema.multiresumeOrder).where(inArray(schema.multiresumeOrder.id, orderIds));
    for (const row of rows) ordersById.set(row.id, mapOrder(row));
  }

  return ledgerEntries.map((entry) => {
    const aiJob = entry.aiJobId
      ? aiJobRowsById.get(entry.aiJobId)
      : entry.reservationId
        ? aiJobRowsByReservationId.get(entry.reservationId)
        : null;
    const order = entry.orderId ? ordersById.get(entry.orderId) : undefined;

    return {
      ...entry,
      aiAction: (aiJob?.action as AiAction | undefined) ?? null,
      aiJobStatus: (aiJob?.status as QuotaAiJobStatus | undefined) ?? null,
      resumeName: aiJob?.resumeId ? (resumeNamesById.get(aiJob.resumeId) ?? null) : null,
      targetResumeName: aiJob?.targetResumeId ? (resumeNamesById.get(aiJob.targetResumeId) ?? null) : null,
      orderShortId: order?.shortId ?? null,
      orderPackageDisplayName: getOrderPackageDisplayName(order),
    };
  });
}

function mapAbnormalEvent(row: typeof schema.multiresumeAbnormalEvent.$inferSelect): AbnormalPaymentEventRecord {
  return row;
}

function snapshotPackage(input: BillingPackageRecord) {
  return {
    packageId: input.packageId,
    version: input.version,
    displayName: input.displayName,
    purchaseType: input.purchaseType,
    quotaUnits: input.quotaUnits,
    validityDays: input.validityDays ?? null,
    currency: input.currency,
    amountMinor: input.amountMinor ?? null,
    priceDisplay: input.priceDisplay,
    paddleEnvironment: input.paddleEnvironment,
    paddlePriceId: input.paddlePriceId ?? null,
    perAccountPurchaseCap: input.perAccountPurchaseCap ?? null,
  };
}

function snapshotWhitelist(input: PurchaseWhitelistRecord) {
  return {
    status: input.status,
    allowedPackages: input.allowedPackages,
    allowedUntil: input.allowedUntil?.toISOString() ?? null,
    purchaseCap: input.purchaseCap ?? null,
    reason: input.reason,
  };
}

function checkoutConsentAuthority(value: Record<string, unknown>): {
  fingerprint: string;
  packageId: string;
  idempotencyKey: string;
} | null {
  const candidate = value.checkoutConsent;
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return null;
  const record = candidate as Record<string, unknown>;
  if (
    typeof record.fingerprint !== "string" ||
    typeof record.packageId !== "string" ||
    typeof record.idempotencyKey !== "string"
  ) {
    return null;
  }
  return {
    fingerprint: record.fingerprint,
    packageId: record.packageId,
    idempotencyKey: record.idempotencyKey,
  };
}

export function matchesWaffoCheckoutFenceAuthority(order: OrderRecord, input: WaffoCheckoutFenceClaimInput) {
  const storedConsent = checkoutConsentAuthority(order.disclosureVersions);
  const requestedConsent = checkoutConsentAuthority(input.disclosureVersions);
  return (
    order.userId === input.userId &&
    order.idempotencyKey === input.idempotencyKey &&
    order.packageId === input.package.packageId &&
    order.packageVersion === input.package.version &&
    stableJson(order.packageSnapshot) === stableJson(snapshotPackage(input.package)) &&
    order.provider === input.provider &&
    order.providerEnvironment === input.providerEnvironment &&
    storedConsent !== null &&
    requestedConsent !== null &&
    storedConsent.fingerprint === requestedConsent.fingerprint &&
    storedConsent.packageId === requestedConsent.packageId &&
    storedConsent.idempotencyKey === requestedConsent.idempotencyKey
  );
}

export const unfinishedOrderStatuses = [
  "created",
  "checkout_pending",
  "checkout_opened",
  "paid_processing",
  "payment_pending",
] as const;
const waffoCheckoutFenceStatuses = [...unfinishedOrderStatuses, "needs_review"] as const;
export type UnfinishedOrderStatus = (typeof unfinishedOrderStatuses)[number];
export const checkoutAttachmentStatuses = [...unfinishedOrderStatuses, "failed"] as const;
export type CheckoutAttachmentStatus = (typeof checkoutAttachmentStatuses)[number];

export function isUnfinishedOrderStatus(status: string): status is UnfinishedOrderStatus {
  return (unfinishedOrderStatuses as readonly string[]).includes(status);
}

export function isCheckoutAttachmentStatus(status: string): status is CheckoutAttachmentStatus {
  return (checkoutAttachmentStatuses as readonly string[]).includes(status);
}

export function createDrizzlePaymentStore(database?: Awaited<ReturnType<typeof getDefaultDb>>): PaymentStore {
  const getDb = async () => database ?? (await getDefaultDb());
  return {
    async listLedgerEntries(input) {
      const db = await getDb();
      const rows = await db
        .select()
        .from(schema.multiresumeQuotaLedgerEntry)
        .where(eq(schema.multiresumeQuotaLedgerEntry.userId, input.userId));

      return enrichLedgerEntriesForHistory(rows.map(mapLedgerEntry));
    },

    async findPurchaseWhitelist(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumePurchaseWhitelist)
        .where(eq(schema.multiresumePurchaseWhitelist.userId, input.userId))
        .limit(1);

      return row ? mapWhitelist(row) : null;
    },

    async findActivePackage(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumeBillingPackage)
        .where(
          and(
            eq(schema.multiresumeBillingPackage.packageId, input.packageId),
            eq(schema.multiresumeBillingPackage.paddleEnvironment, input.environment),
            eq(schema.multiresumeBillingPackage.status, "active"),
          ),
        )
        .limit(1);

      return row ? mapPackage(row) : null;
    },

    async listOrders(input) {
      const db = await getDb();
      const rows = await db
        .select()
        .from(schema.multiresumeOrder)
        .where(eq(schema.multiresumeOrder.userId, input.userId))
        .orderBy(desc(schema.multiresumeOrder.createdAt))
        .limit(input.limit ?? 20);

      return rows.map(mapOrder);
    },

    async findOrderForUser(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumeOrder)
        .where(and(eq(schema.multiresumeOrder.id, input.orderId), eq(schema.multiresumeOrder.userId, input.userId)))
        .limit(1);
      return row ? mapOrder(row) : null;
    },

    async countGrantedOrdersForPackage(input) {
      const db = await getDb();
      const rows = await db
        .select({ id: schema.multiresumeOrder.id })
        .from(schema.multiresumeOrder)
        .where(
          and(
            eq(schema.multiresumeOrder.userId, input.userId),
            eq(schema.multiresumeOrder.packageId, input.packageId),
            eq(schema.multiresumeOrder.quotaGrantStatus, "granted"),
          ),
        );

      return rows.length;
    },

    async findPendingOrder(input) {
      const db = await getDb();
      const rows = await db
        .select()
        .from(schema.multiresumeOrder)
        .where(
          and(
            eq(schema.multiresumeOrder.userId, input.userId),
            ...(input.packageId ? [eq(schema.multiresumeOrder.packageId, input.packageId)] : []),
          ),
        )
        .orderBy(desc(schema.multiresumeOrder.createdAt));

      const row = rows.find((order) => isUnfinishedOrderStatus(order.status) || order.status === "needs_review");
      return row ? mapOrder(row) : null;
    },

    async findOrderByIdempotencyKey(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumeOrder)
        .where(eq(schema.multiresumeOrder.idempotencyKey, input.idempotencyKey))
        .limit(1);

      return row ? mapOrder(row) : null;
    },

    async createOrder(input) {
      const db = await getDb();
      const [row] = await db
        .insert(schema.multiresumeOrder)
        .values({
          shortId: input.shortId,
          userId: input.userId,
          packageId: input.package.packageId,
          packageVersion: input.package.version,
          packageSnapshot: snapshotPackage(input.package),
          expectedQuotaUnits: input.package.quotaUnits,
          expectedCurrency: input.package.currency,
          expectedAmountMinor: input.package.amountMinor,
          expectedPriceDisplay: input.package.priceDisplay,
          expectedPaddlePriceId: input.package.paddlePriceId,
          paddleEnvironment: input.package.paddleEnvironment,
          provider: input.provider,
          providerEnvironment: input.providerEnvironment,
          status: "created",
          quotaGrantStatus: "not_granted",
          refundStatus: "not_requested",
          purchaseWhitelistSnapshot: snapshotWhitelist(input.whitelist),
          disclosureVersions: input.disclosureVersions,
          idempotencyKey: input.idempotencyKey,
          createdAt: input.now,
          updatedAt: input.now,
        })
        .onConflictDoUpdate({
          target: [schema.multiresumeOrder.idempotencyKey],
          set: { updatedAt: sql`now()` },
        })
        .returning();

      if (!row) throw new Error("Failed to create billing order.");
      return mapOrder(row);
    },

    async claimWaffoCheckoutFence(input) {
      const db = await getDb();
      const leaseMs = Math.min(Math.max(input.leaseMs ?? 60_000, 15_000), 5 * 60_000);
      const leaseExpiresAt = new Date(input.now.getTime() + leaseMs);
      const lockKey = `checkout:pending-user:${input.userId}`;

      return await db.transaction(async (tx) => {
        await tx.execute(sql`SELECT pg_advisory_xact_lock(hashtextextended(${lockKey}, 0))`);

        const [byIdempotencyKey] = await tx
          .select()
          .from(schema.multiresumeOrder)
          .where(eq(schema.multiresumeOrder.idempotencyKey, input.idempotencyKey))
          .limit(1)
          .for("update");
        const unfinished = await tx
          .select()
          .from(schema.multiresumeOrder)
          .where(
            and(
              eq(schema.multiresumeOrder.userId, input.userId),
              inArray(schema.multiresumeOrder.status, waffoCheckoutFenceStatuses),
            ),
          )
          .orderBy(desc(schema.multiresumeOrder.createdAt))
          .limit(1)
          .for("update");
        const currentUnfinished = unfinished[0] ?? null;
        if (byIdempotencyKey && !matchesWaffoCheckoutFenceAuthority(mapOrder(byIdempotencyKey), input)) {
          return { kind: "needs_review" as const, order: mapOrder(byIdempotencyKey) };
        }
        if (currentUnfinished && (!byIdempotencyKey || currentUnfinished.id !== byIdempotencyKey.id)) {
          return { kind: "in_progress" as const, order: mapOrder(currentUnfinished) };
        }
        const current = byIdempotencyKey ?? currentUnfinished;

        if (current && isUnfinishedOrderStatus(current.status)) {
          const order = mapOrder(current);
          if (
            order.providerCheckoutSessionId &&
            order.providerCheckoutUrl &&
            order.providerCheckoutExpiresAt &&
            order.providerCheckoutExpiresAt.getTime() > input.now.getTime()
          ) {
            return { kind: "ready" as const, order };
          }
          // Once a dispatch has begun, Waffo does not expose a documented
          // idempotent session lookup. An unexpired owner is still active; an
          // expired owner is durably escalated before any replacement can act.
          if (order.checkoutDispatchStartedAt) {
            if (
              order.checkoutDispatchLeaseExpiresAt &&
              order.checkoutDispatchLeaseExpiresAt.getTime() > input.now.getTime()
            ) {
              return { kind: "in_progress" as const, order };
            }
            const [reviewed] = await tx
              .update(schema.multiresumeOrder)
              .set({
                status: "needs_review",
                updatedAt: input.now,
              })
              .where(
                and(
                  eq(schema.multiresumeOrder.id, order.id),
                  eq(schema.multiresumeOrder.checkoutDispatchLeaseId, order.checkoutDispatchLeaseId ?? ""),
                  lte(schema.multiresumeOrder.checkoutDispatchLeaseExpiresAt, input.now),
                  inArray(schema.multiresumeOrder.status, unfinishedOrderStatuses),
                ),
              )
              .returning();
            return { kind: "needs_review" as const, order: mapOrder(reviewed ?? current) };
          }
          if (
            order.checkoutDispatchLeaseExpiresAt &&
            order.checkoutDispatchLeaseExpiresAt.getTime() > input.now.getTime()
          ) {
            return { kind: "in_progress" as const, order };
          }

          const checkoutDispatchLeaseId = randomUUID();
          const [claimed] = await tx
            .update(schema.multiresumeOrder)
            .set({
              checkoutDispatchLeaseId,
              checkoutDispatchLeaseExpiresAt: leaseExpiresAt,
              updatedAt: input.now,
            })
            .where(
              and(eq(schema.multiresumeOrder.id, order.id), isNull(schema.multiresumeOrder.checkoutDispatchStartedAt)),
            )
            .returning();
          if (!claimed) return { kind: "needs_review" as const, order };
          return { kind: "claimed" as const, order: mapOrder(claimed), checkoutDispatchLeaseId };
        }
        if (current?.status === "needs_review") return { kind: "needs_review" as const, order: mapOrder(current) };

        const checkoutDispatchLeaseId = randomUUID();
        const [created] = await tx
          .insert(schema.multiresumeOrder)
          .values({
            shortId: input.shortId,
            userId: input.userId,
            packageId: input.package.packageId,
            packageVersion: input.package.version,
            packageSnapshot: snapshotPackage(input.package),
            expectedQuotaUnits: input.package.quotaUnits,
            expectedCurrency: input.package.currency,
            expectedAmountMinor: input.package.amountMinor,
            expectedPriceDisplay: input.package.priceDisplay,
            expectedPaddlePriceId: input.package.paddlePriceId,
            paddleEnvironment: input.package.paddleEnvironment,
            provider: input.provider,
            providerEnvironment: input.providerEnvironment,
            status: "created",
            quotaGrantStatus: "not_granted",
            refundStatus: "not_requested",
            purchaseWhitelistSnapshot: snapshotWhitelist(input.whitelist),
            disclosureVersions: input.disclosureVersions,
            idempotencyKey: input.idempotencyKey,
            checkoutDispatchLeaseId,
            checkoutDispatchLeaseExpiresAt: leaseExpiresAt,
            createdAt: input.now,
            updatedAt: input.now,
          })
          .returning();
        if (!created) throw new Error("Failed to claim Waffo checkout fence.");
        return { kind: "claimed" as const, order: mapOrder(created), checkoutDispatchLeaseId };
      });
    },

    async markWaffoCheckoutDispatchStarted(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumeOrder)
        .set({ checkoutDispatchStartedAt: input.now, updatedAt: input.now })
        .where(
          and(
            eq(schema.multiresumeOrder.id, input.orderId),
            eq(schema.multiresumeOrder.checkoutDispatchLeaseId, input.checkoutDispatchLeaseId),
            isNull(schema.multiresumeOrder.checkoutDispatchStartedAt),
            gt(schema.multiresumeOrder.checkoutDispatchLeaseExpiresAt, input.now),
          ),
        )
        .returning();
      if (!row) throw new Error("Waffo checkout dispatch lease was lost.");
      return mapOrder(row);
    },

    async attachCheckout(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumeOrder)
        .set({
          paddleTransactionId: input.paddleTransactionId,
          paddleCustomerId: input.paddleCustomerId,
          paddleCheckoutUrl: input.checkoutUrl,
          status: "checkout_pending",
          checkoutOpenedAt: input.now,
          updatedAt: input.now,
        })
        .where(
          and(
            eq(schema.multiresumeOrder.id, input.orderId),
            ...(input.expectedUpdatedAt ? [eq(schema.multiresumeOrder.updatedAt, input.expectedUpdatedAt)] : []),
            ...(input.expectedProvider ? [eq(schema.multiresumeOrder.provider, input.expectedProvider)] : []),
            ...(input.expectedProviderEnvironment
              ? [eq(schema.multiresumeOrder.providerEnvironment, input.expectedProviderEnvironment)]
              : []),
            ...(input.expectedStatus ? [eq(schema.multiresumeOrder.status, input.expectedStatus)] : []),
          ),
        )
        .returning();

      if (!row) throw new Error("Failed to attach Paddle checkout to order.");
      return mapOrder(row);
    },

    async attachProviderCheckout(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumeOrder)
        .set({
          providerStoreId: input.providerStoreId,
          providerProductId: input.providerProductId,
          providerCheckoutSessionId: input.providerCheckoutSessionId,
          providerCheckoutUrl: input.providerCheckoutUrl,
          providerCheckoutExpiresAt: input.providerCheckoutExpiresAt ?? null,
          orderMerchantExternalId: input.orderMerchantExternalId,
          status: "checkout_pending",
          checkoutOpenedAt: input.now,
          updatedAt: input.now,
        })
        .where(
          and(
            eq(schema.multiresumeOrder.id, input.orderId),
            ...(input.checkoutDispatchLeaseId
              ? [eq(schema.multiresumeOrder.checkoutDispatchLeaseId, input.checkoutDispatchLeaseId)]
              : []),
            ...(input.expectedUpdatedAt ? [eq(schema.multiresumeOrder.updatedAt, input.expectedUpdatedAt)] : []),
            ...(input.expectedProvider ? [eq(schema.multiresumeOrder.provider, input.expectedProvider)] : []),
            ...(input.expectedProviderEnvironment
              ? [eq(schema.multiresumeOrder.providerEnvironment, input.expectedProviderEnvironment)]
              : []),
            ...(input.expectedStatus ? [eq(schema.multiresumeOrder.status, input.expectedStatus)] : []),
          ),
        )
        .returning();

      if (!row) throw new Error("Failed to attach provider checkout to order.");
      return mapOrder(row);
    },

    async markOrderCheckoutFailed(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumeOrder)
        .set({
          status: "failed",
          failedAt: input.now,
          updatedAt: input.now,
        })
        .where(
          and(
            eq(schema.multiresumeOrder.id, input.orderId),
            ...(input.expectedUpdatedAt ? [eq(schema.multiresumeOrder.updatedAt, input.expectedUpdatedAt)] : []),
            ...(input.expectedProvider ? [eq(schema.multiresumeOrder.provider, input.expectedProvider)] : []),
            ...(input.expectedProviderEnvironment
              ? [eq(schema.multiresumeOrder.providerEnvironment, input.expectedProviderEnvironment)]
              : []),
            ...(input.expectedStatus ? [eq(schema.multiresumeOrder.status, input.expectedStatus)] : []),
          ),
        )
        .returning();

      if (!row) throw new Error("Failed to mark checkout order failed.");
      return mapOrder(row);
    },

    async findOrderByPaddleTransactionId(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumeOrder)
        .where(eq(schema.multiresumeOrder.paddleTransactionId, input.paddleTransactionId))
        .limit(1);

      return row ? mapOrder(row) : null;
    },

    async findOrderByProviderLinkage(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumeOrder)
        .where(
          and(
            eq(schema.multiresumeOrder.provider, input.provider),
            eq(schema.multiresumeOrder.providerEnvironment, input.environment),
            eq(schema.multiresumeOrder.orderMerchantExternalId, input.orderMerchantExternalId),
          ),
        )
        .limit(1);

      return row ? mapOrder(row) : null;
    },

    async findPaymentEventByIdempotencyKey(input) {
      const db = await getDb();
      const [row] = await db
        .select()
        .from(schema.multiresumePaymentEvent)
        .where(eq(schema.multiresumePaymentEvent.idempotencyKey, input.idempotencyKey))
        .limit(1);

      return row ? mapPaymentEvent(row) : null;
    },

    async createPaymentEvent(input) {
      const existing = await this.findPaymentEventByIdempotencyKey({ idempotencyKey: input.idempotencyKey });
      if (existing) return { event: existing, duplicate: true };

      const db = await getDb();
      const [row] = await db
        .insert(schema.multiresumePaymentEvent)
        .values({ ...input, provider: "paddle" })
        .onConflictDoNothing()
        .returning();

      if (row) return { event: mapPaymentEvent(row), duplicate: false };

      const duplicate = await this.findPaymentEventByIdempotencyKey({ idempotencyKey: input.idempotencyKey });
      if (!duplicate) throw new Error("Failed to persist payment event.");
      return { event: duplicate, duplicate: true };
    },

    async createProviderPaymentEvent(input) {
      const db = await getDb();
      return await db.transaction(async (tx) => {
        // Lock every identity that can represent one provider payment. The
        // sorted order prevents deadlocks when concurrent deliveries use
        // different keys but collide on a unique delivery/business/payment
        // identity.
        const lockKeys = [
          `${input.provider}:${input.environment}:delivery:${input.providerDeliveryId}`,
          `${input.provider}:${input.environment}:business:${input.eventType}:${input.providerBusinessEventId}`,
          ...(input.providerPaymentId
            ? [
                providerPaymentIdentityLockKey({
                  provider: input.provider,
                  environment: input.environment,
                  eventType: input.eventType,
                  providerPaymentId: input.providerPaymentId,
                }),
              ]
            : []),
          `${input.provider}:${input.environment}:idempotency:${input.idempotencyKey}`,
        ].sort();
        for (const lockKey of lockKeys) {
          await tx.execute(sql`SELECT pg_advisory_xact_lock(hashtextextended(${lockKey}, 0))`);
        }

        const identity = and(
          eq(schema.multiresumePaymentEvent.provider, input.provider),
          eq(schema.multiresumePaymentEvent.environment, input.environment),
        );

        const findExisting = async () =>
          await tx
            .select()
            .from(schema.multiresumePaymentEvent)
            .where(
              and(
                identity,
                or(
                  eq(schema.multiresumePaymentEvent.providerDeliveryId, input.providerDeliveryId),
                  eq(schema.multiresumePaymentEvent.providerBusinessEventId, input.providerBusinessEventId),
                  eq(schema.multiresumePaymentEvent.idempotencyKey, input.idempotencyKey),
                  ...(input.providerPaymentId
                    ? [
                        and(
                          eq(schema.multiresumePaymentEvent.eventType, input.eventType),
                          eq(schema.multiresumePaymentEvent.providerPaymentId, input.providerPaymentId),
                        ),
                      ]
                    : []),
                ),
              ),
            )
            .limit(8);

        const resolveExisting = (rows: (typeof schema.multiresumePaymentEvent.$inferSelect)[]) => {
          const existing = rows[0];
          if (!existing) return null;
          const conflict = rows.some(
            (row) => existingProviderAuthorityFingerprint(row) !== providerAuthorityFingerprint(input),
          );
          return {
            event: mapPaymentEvent(existing),
            duplicate: true as const,
            ...(conflict
              ? {
                  conflict: {
                    reasonCode: "waffo_provider_identity_conflict" as const,
                    summary: "Waffo provider identity fields conflict with an existing durable event." as const,
                  },
                }
              : {}),
          };
        };

        const existingRows = await findExisting();
        const inquiryFact = existingRows.find(
          (row) =>
            row.signatureStatus === "provider_inquiry_verified" &&
            input.signatureStatus === "verified" &&
            row.eventType === input.eventType &&
            row.providerStoreId === (input.providerStoreId ?? null) &&
            row.orderMerchantExternalId === (input.orderMerchantExternalId ?? null) &&
            row.providerOrderId === (input.providerOrderId ?? null) &&
            row.providerPaymentId === (input.providerPaymentId ?? null) &&
            (!input.orderId || row.orderId === input.orderId),
        );
        if (inquiryFact) {
          const [promoted] = await tx
            .update(schema.multiresumePaymentEvent)
            .set({
              providerDeliveryId: input.providerDeliveryId,
              providerBusinessEventId: input.providerBusinessEventId,
              occurredAt: input.occurredAt ?? inquiryFact.occurredAt,
              signatureStatus: "verified",
              redactedPayload: input.redactedPayload ?? inquiryFact.redactedPayload,
              updatedAt: input.receivedAt,
            })
            .where(eq(schema.multiresumePaymentEvent.id, inquiryFact.id))
            .returning();
          if (!promoted) throw new Error("Failed to converge signed webhook with provider inquiry payment fact.");
          return { event: mapPaymentEvent(promoted), duplicate: true };
        }

        const existingResult = resolveExisting(existingRows);
        if (existingResult) return existingResult;

        const [row] = await tx.insert(schema.multiresumePaymentEvent).values(input).onConflictDoNothing().returning();
        if (row) return { event: mapPaymentEvent(row), duplicate: false };
        const concurrentResult = resolveExisting(await findExisting());
        if (concurrentResult) return concurrentResult;
        throw new Error("Failed to persist provider payment event.");
      });
    },

    async recordProviderIdentityConflict(input) {
      const db = await getDb();
      return await db.transaction(async (tx) => {
        await tx.execute(
          sql`SELECT 1 FROM "multiresume_payment_events" WHERE "id" = ${input.paymentEventId} FOR UPDATE`,
        );
        const [currentEvent] = await tx
          .select()
          .from(schema.multiresumePaymentEvent)
          .where(eq(schema.multiresumePaymentEvent.id, input.paymentEventId))
          .limit(1);
        if (!currentEvent) throw new Error("Missing payment event for identity conflict.");

        let userId: string | null = null;
        if (currentEvent.orderId) {
          const [order] = await tx
            .select({ userId: schema.multiresumeOrder.userId })
            .from(schema.multiresumeOrder)
            .where(eq(schema.multiresumeOrder.id, currentEvent.orderId))
            .limit(1);
          userId = order?.userId ?? null;
        }

        const [existingAbnormal] = await tx
          .select()
          .from(schema.multiresumeAbnormalEvent)
          .where(
            and(
              eq(schema.multiresumeAbnormalEvent.paymentEventId, currentEvent.id),
              eq(schema.multiresumeAbnormalEvent.reasonCode, input.reasonCode),
              eq(schema.multiresumeAbnormalEvent.status, "open"),
            ),
          )
          .limit(1);
        if (!existingAbnormal) {
          await tx.insert(schema.multiresumeAbnormalEvent).values({
            group: "payment",
            severity: "high",
            status: "open",
            userId,
            orderId: currentEvent.orderId,
            paymentEventId: currentEvent.id,
            reasonCode: input.reasonCode,
            summary: input.summary,
            createdAt: input.now,
            updatedAt: input.now,
          });
        }

        if (currentEvent.processingStatus === "processed") return mapPaymentEvent(currentEvent);

        const [updatedEvent] = await tx
          .update(schema.multiresumePaymentEvent)
          .set({
            processingStatus: "needs_review",
            processingLeaseId: null,
            processedAt: input.now,
            errorType: input.reasonCode,
            errorMessage: input.summary,
            updatedAt: input.now,
          })
          .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
          .returning();
        if (!updatedEvent) throw new Error("Failed to persist provider identity conflict.");
        return mapPaymentEvent(updatedEvent);
      });
    },

    async claimProviderPaymentEvent(input) {
      const db = await getDb();
      return await db.transaction(async (tx) => {
        await tx.execute(
          sql`SELECT 1 FROM "multiresume_payment_events" WHERE "id" = ${input.paymentEventId} FOR UPDATE`,
        );
        const [currentEvent] = await tx
          .select()
          .from(schema.multiresumePaymentEvent)
          .where(eq(schema.multiresumePaymentEvent.id, input.paymentEventId))
          .limit(1);
        if (!currentEvent) throw new Error("Missing payment event for processing claim.");

        const staleProcessingClaim =
          currentEvent.processingStatus === "processing" &&
          input.now.getTime() - currentEvent.updatedAt.getTime() >= 60_000;
        if (currentEvent.processingStatus !== "received" && !staleProcessingClaim) {
          return { event: mapPaymentEvent(currentEvent), claimed: false };
        }

        const [claimedEvent] = await tx
          .update(schema.multiresumePaymentEvent)
          .set({
            processingStatus: "processing",
            processingLeaseId: randomUUID(),
            updatedAt: input.now,
          })
          .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
          .returning();
        if (!claimedEvent) throw new Error("Failed to claim payment event.");
        return { event: mapPaymentEvent(claimedEvent), claimed: true };
      });
    },

    async releaseProviderPaymentEventClaim(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumePaymentEvent)
        .set({ processingStatus: "received", processingLeaseId: null, updatedAt: input.now })
        .where(
          and(
            eq(schema.multiresumePaymentEvent.id, input.paymentEventId),
            eq(schema.multiresumePaymentEvent.processingStatus, "processing"),
            eq(schema.multiresumePaymentEvent.processingLeaseId, input.processingLeaseId),
          ),
        )
        .returning();
      if (!row) throw new Error("Failed to release payment event claim.");
      return mapPaymentEvent(row);
    },

    async markPaymentEventProcessed(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumePaymentEvent)
        .set({
          ...(input.orderId !== undefined ? { orderId: input.orderId } : {}),
          processingStatus: input.processingStatus,
          processedAt: input.now,
          errorType: input.errorType,
          errorMessage: input.errorMessage,
          processingLeaseId: null,
          updatedAt: input.now,
        })
        .where(eq(schema.multiresumePaymentEvent.id, input.paymentEventId))
        .returning();

      if (!row) throw new Error("Failed to update payment event.");
      return mapPaymentEvent(row);
    },

    async markOrderPaidProcessing(input) {
      const db = await getDb();
      return await db.transaction(async (tx) => {
        await tx.execute(sql`SELECT 1 FROM "multiresume_orders" WHERE "id" = ${input.orderId} FOR UPDATE`);
        const [currentOrder] = await tx
          .select()
          .from(schema.multiresumeOrder)
          .where(eq(schema.multiresumeOrder.id, input.orderId))
          .limit(1);
        if (!currentOrder) throw new Error("Failed to mark order paid processing.");
        if (
          (currentOrder.providerOrderId &&
            input.providerOrderId &&
            currentOrder.providerOrderId !== input.providerOrderId) ||
          (currentOrder.providerPaymentId &&
            input.providerPaymentId &&
            currentOrder.providerPaymentId !== input.providerPaymentId)
        ) {
          throw new WaffoProviderBindingConflictError();
        }

        const [row] = await tx
          .update(schema.multiresumeOrder)
          .set({
            status: "paid_processing",
            ...(input.providerOrderId ? { providerOrderId: input.providerOrderId } : {}),
            ...(input.providerPaymentId ? { providerPaymentId: input.providerPaymentId } : {}),
            paidAt: input.now,
            updatedAt: input.now,
          })
          .where(eq(schema.multiresumeOrder.id, input.orderId))
          .returning();

        if (!row) throw new Error("Failed to mark order paid processing.");
        return mapOrder(row);
      });
    },

    async markOrderNeedsReview(input) {
      const db = await getDb();
      const [row] = await db
        .update(schema.multiresumeOrder)
        .set({
          status: "needs_review",
          quotaGrantStatus: input.quotaGrantStatus ?? "needs_review",
          updatedAt: input.now,
        })
        .where(
          and(
            eq(schema.multiresumeOrder.id, input.orderId),
            ...(input.expectedCheckoutDispatchLeaseId
              ? [eq(schema.multiresumeOrder.checkoutDispatchLeaseId, input.expectedCheckoutDispatchLeaseId)]
              : []),
            ...(input.expectedUpdatedAt ? [eq(schema.multiresumeOrder.updatedAt, input.expectedUpdatedAt)] : []),
            ...(input.expectedStatus ? [eq(schema.multiresumeOrder.status, input.expectedStatus)] : []),
            ...(input.expectedStatus ? [inArray(schema.multiresumeOrder.status, unfinishedOrderStatuses)] : []),
          ),
        )
        .returning();

      if (!row) throw new Error("Failed to mark order needs_review.");
      return mapOrder(row);
    },

    async recordRefundOutcome(input) {
      const db = await getDb();
      return await db.transaction(async (tx) => {
        await tx.execute(
          sql`SELECT 1 FROM "multiresume_payment_events" WHERE "id" = ${input.paymentEvent.id} FOR UPDATE`,
        );
        const [currentEvent] = await tx
          .select()
          .from(schema.multiresumePaymentEvent)
          .where(eq(schema.multiresumePaymentEvent.id, input.paymentEvent.id))
          .limit(1);
        if (!currentEvent) throw new Error("Missing payment event for refund outcome.");

        let currentOrder: typeof schema.multiresumeOrder.$inferSelect | null = null;
        if (input.order) {
          await tx.execute(sql`SELECT 1 FROM "multiresume_orders" WHERE "id" = ${input.order.id} FOR UPDATE`);
          [currentOrder] = await tx
            .select()
            .from(schema.multiresumeOrder)
            .where(eq(schema.multiresumeOrder.id, input.order.id))
            .limit(1);
          if (!currentOrder) throw new Error("Missing order for refund outcome.");
        }

        if (currentEvent.processingStatus !== "received") {
          return {
            order: currentOrder ? mapOrder(currentOrder) : null,
            paymentEvent: mapPaymentEvent(currentEvent),
            abnormalEvent: null,
          };
        }

        const refundOutcomeConflict = currentOrder?.refundStatus === "refunded" && input.refundStatus === "failed";
        const effectiveNeedsReview = Boolean(input.needsReview || refundOutcomeConflict);
        const effectiveReasonCode = refundOutcomeConflict
          ? "waffo_refund_outcome_conflict"
          : (input.reasonCode ?? "waffo_refund_needs_review");
        const effectiveSummary = refundOutcomeConflict
          ? "A failed Waffo refund outcome conflicts with an already recorded succeeded refund."
          : (input.summary ?? "Waffo refund needs operator review.");

        let updatedOrder = currentOrder;
        if (currentOrder) {
          const [row] = await tx
            .update(schema.multiresumeOrder)
            .set({
              ...(input.refundStatus && !refundOutcomeConflict ? { refundStatus: input.refundStatus } : {}),
              ...(effectiveNeedsReview ? { status: "needs_review" } : {}),
              updatedAt: input.now,
            })
            .where(eq(schema.multiresumeOrder.id, currentOrder.id))
            .returning();
          if (!row) throw new Error("Failed to record order refund outcome.");
          updatedOrder = row;
        }

        let abnormalEvent: AbnormalPaymentEventRecord | null = null;
        if (effectiveNeedsReview) {
          const [row] = await tx
            .insert(schema.multiresumeAbnormalEvent)
            .values({
              group: "payment",
              severity: "high",
              status: "open",
              userId: updatedOrder?.userId ?? null,
              orderId: updatedOrder?.id ?? null,
              paymentEventId: currentEvent.id,
              reasonCode: effectiveReasonCode,
              summary: effectiveSummary,
              createdAt: input.now,
              updatedAt: input.now,
            })
            .returning();
          if (!row) throw new Error("Failed to create refund review event.");
          abnormalEvent = mapAbnormalEvent(row);
        }

        const [processedEvent] = await tx
          .update(schema.multiresumePaymentEvent)
          .set({
            ...(currentOrder ? { orderId: currentOrder.id } : {}),
            processingStatus: effectiveNeedsReview ? "needs_review" : "processed",
            processedAt: input.now,
            errorType: effectiveNeedsReview ? effectiveReasonCode : null,
            errorMessage: effectiveNeedsReview ? effectiveSummary : null,
            updatedAt: input.now,
          })
          .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
          .returning();
        if (!processedEvent) throw new Error("Failed to complete refund payment event.");

        return {
          order: updatedOrder ? mapOrder(updatedOrder) : null,
          paymentEvent: mapPaymentEvent(processedEvent),
          abnormalEvent,
        };
      });
    },

    async grantPaidQuota(input) {
      const db = await getDb();
      return await db.transaction(async (tx) => {
        let currentEvent: typeof schema.multiresumePaymentEvent.$inferSelect | null = null;
        if (input.processingLeaseId) {
          await tx.execute(
            sql`SELECT 1 FROM "multiresume_payment_events" WHERE "id" = ${input.paymentEvent.id} FOR UPDATE`,
          );
          [currentEvent] = await tx
            .select()
            .from(schema.multiresumePaymentEvent)
            .where(eq(schema.multiresumePaymentEvent.id, input.paymentEvent.id))
            .limit(1);
          if (
            !currentEvent ||
            currentEvent.processingStatus !== "processing" ||
            currentEvent.processingLeaseId !== input.processingLeaseId
          ) {
            throw new WaffoPaymentEventClaimLostError(
              currentEvent ? mapPaymentEvent(currentEvent) : input.paymentEvent,
            );
          }
        }
        await tx.execute(sql`SELECT 1 FROM "multiresume_orders" WHERE "id" = ${input.order.id} FOR UPDATE`);

        const [currentOrder] = await tx
          .select()
          .from(schema.multiresumeOrder)
          .where(eq(schema.multiresumeOrder.id, input.order.id))
          .limit(1);
        if (!currentOrder) throw new Error("Missing order for quota grant.");

        if (
          (currentOrder.providerOrderId &&
            (input.providerOrderId ?? input.paymentEvent.providerOrderId) &&
            currentOrder.providerOrderId !== (input.providerOrderId ?? input.paymentEvent.providerOrderId)) ||
          (currentOrder.providerPaymentId &&
            (input.providerPaymentId ?? input.paymentEvent.providerPaymentId) &&
            currentOrder.providerPaymentId !== (input.providerPaymentId ?? input.paymentEvent.providerPaymentId))
        ) {
          throw new WaffoProviderBindingConflictError();
        }

        if (currentOrder.refundStatus === "refunded" && currentOrder.quotaGrantStatus !== "granted") {
          throw new WaffoRefundFirstHoldError();
        }

        const grantIdempotencyKey = `quota-grant:order:${currentOrder.id}`;
        const existingGrants = await tx
          .select()
          .from(schema.multiresumeQuotaLedgerEntry)
          .where(
            or(
              eq(schema.multiresumeQuotaLedgerEntry.idempotencyKey, grantIdempotencyKey),
              and(
                eq(schema.multiresumeQuotaLedgerEntry.orderId, currentOrder.id),
                eq(schema.multiresumeQuotaLedgerEntry.entryType, "purchase_grant"),
              ),
            ),
          )
          .orderBy(schema.multiresumeQuotaLedgerEntry.id)
          .for("update");
        const existingLedger = existingGrants[0];

        if (
          existingGrants.length > 1 ||
          (existingLedger && !matchesPaidQuotaGrantAuthority(existingLedger, currentOrder))
        ) {
          throw new PaidQuotaGrantAuthorityError("grant_ledger_mismatch");
        }
        if (currentOrder.quotaGrantStatus === "granted" && !existingLedger) {
          throw new PaidQuotaGrantAuthorityError("grant_ledger_mismatch");
        }

        if (existingLedger) {
          const [updatedOrder] = await tx
            .update(schema.multiresumeOrder)
            .set({
              status: "completed",
              quotaGrantStatus: "granted",
              ...((input.providerOrderId ?? input.paymentEvent.providerOrderId)
                ? { providerOrderId: input.providerOrderId ?? input.paymentEvent.providerOrderId }
                : {}),
              ...((input.providerPaymentId ?? input.paymentEvent.providerPaymentId)
                ? { providerPaymentId: input.providerPaymentId ?? input.paymentEvent.providerPaymentId }
                : {}),
              completedAt: currentOrder.completedAt ?? input.now,
              updatedAt: input.now,
            })
            .where(eq(schema.multiresumeOrder.id, currentOrder.id))
            .returning();
          if (!updatedOrder) throw new Error("Failed to confirm granted order.");

          if (currentEvent) {
            const [processedEvent] = await tx
              .update(schema.multiresumePaymentEvent)
              .set({
                orderId: currentOrder.id,
                processingStatus: "processed",
                processingLeaseId: null,
                processedAt: input.now,
                errorType: null,
                errorMessage: null,
                updatedAt: input.now,
              })
              .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
              .returning();
            if (!processedEvent) throw new Error("Failed to complete payment event.");
          }

          return {
            order: mapOrder(updatedOrder),
            ledgerEntry: existingLedger ? mapLedgerEntry(existingLedger) : null,
            paymentEvent: currentEvent
              ? mapPaymentEvent(
                  (
                    await tx
                      .select()
                      .from(schema.multiresumePaymentEvent)
                      .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
                      .limit(1)
                  )[0]!,
                )
              : undefined,
          };
        }

        const [ledgerEntry] = await tx
          .insert(schema.multiresumeQuotaLedgerEntry)
          .values({
            userId: currentOrder.userId,
            entryType: "purchase_grant",
            bucket: "paid",
            amount: currentOrder.expectedQuotaUnits,
            reservedAmount: 0,
            committedAmount: 0,
            sourceType: "order",
            sourceId: currentOrder.id,
            orderId: currentOrder.id,
            paymentEventId: input.paymentEvent.id,
            reasonCode: "provider_payment_completed",
            idempotencyKey: grantIdempotencyKey,
            balanceAfter: null,
            expiresAt: getPaidQuotaGrantExpiresAt(mapOrder(currentOrder), input.now),
          })
          .onConflictDoNothing()
          .returning();

        const [updatedOrder] = await tx
          .update(schema.multiresumeOrder)
          .set({
            status: "completed",
            quotaGrantStatus: ledgerEntry ? "granted" : "quota_grant_failed",
            ...((input.providerOrderId ?? input.paymentEvent.providerOrderId)
              ? { providerOrderId: input.providerOrderId ?? input.paymentEvent.providerOrderId }
              : {}),
            ...((input.providerPaymentId ?? input.paymentEvent.providerPaymentId)
              ? { providerPaymentId: input.providerPaymentId ?? input.paymentEvent.providerPaymentId }
              : {}),
            completedAt: input.now,
            updatedAt: input.now,
          })
          .where(eq(schema.multiresumeOrder.id, currentOrder.id))
          .returning();

        if (!updatedOrder) throw new Error("Failed to complete order.");
        if (currentEvent) {
          const [processedEvent] = await tx
            .update(schema.multiresumePaymentEvent)
            .set({
              orderId: updatedOrder.id,
              processingStatus: "processed",
              processingLeaseId: null,
              processedAt: input.now,
              errorType: null,
              errorMessage: null,
              updatedAt: input.now,
            })
            .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
            .returning();
          if (!processedEvent) throw new Error("Failed to complete payment event.");
        }
        return {
          order: mapOrder(updatedOrder),
          ledgerEntry: ledgerEntry ? mapLedgerEntry(ledgerEntry) : null,
          paymentEvent: currentEvent
            ? mapPaymentEvent(
                (
                  await tx
                    .select()
                    .from(schema.multiresumePaymentEvent)
                    .where(eq(schema.multiresumePaymentEvent.id, currentEvent.id))
                    .limit(1)
                )[0]!,
              )
            : undefined,
        };
      });
    },

    async createAbnormalEvent(input) {
      const db = await getDb();
      const [row] = await db
        .insert(schema.multiresumeAbnormalEvent)
        .values({
          group: "payment",
          severity: input.severity ?? "high",
          status: "open",
          userId: input.userId,
          orderId: input.orderId,
          paymentEventId: input.paymentEventId,
          quotaLedgerEntryId: input.quotaLedgerEntryId,
          reasonCode: input.reasonCode,
          summary: input.summary,
          createdAt: input.now,
          updatedAt: input.now,
        })
        .returning();

      if (!row) throw new Error("Failed to create abnormal payment handoff.");
      return mapAbnormalEvent(row);
    },
  };
}

export const defaultPaymentStore = createDrizzlePaymentStore();
